import { addEvent, triggerEvent, Throttle } from '../utils/events';
import { addClass, hasClass, filterByTagName, setStyles, removeClass } from '../utils/html';
/* eslint-disable no-console */

const DEFAULT_SPLIT_PANEL_OPTIONS = {
  container: '.js-split-panel',
  handle: '.js-splitter',
  launchPanel: '.split-panel__launch',
  backToMainPanel: '.split-panel__back',
};

const PERCENTAGE = 100;
const TIMEOUT_RESIZE_INTERVAL = 20;
const MAGIC_BREAKPOINT = 480;
class SplitPanel {

  constructor(options = {}) {
    options = { ...DEFAULT_SPLIT_PANEL_OPTIONS, ...options };

    this.resizes = [];
    this.currentElement = null;

    this.splitPanel = document.querySelector(options.container);
    this.launchPanelButtons = document.querySelectorAll(options.launchPanel);
    this.backToMainPanelButtons = document.querySelectorAll(options.backToMainPanel);

    const verticalDividers = document.querySelectorAll('.js-vertically-divided');
    const horizontalDividers = document.querySelectorAll('.js-horizontally-divided');

    const verticalArray = Array.from(verticalDividers);
    const horizontalArray = Array.from(horizontalDividers);
    this.resizes = verticalArray.concat(horizontalArray);

    this.setUpSplitters(this.resizes);
  }

  setUpSplitters(resizes) {
    resizes.forEach((elem) => {
      const isVertical = hasClass(elem, 'js-vertically-divided');
      elem.vertical = isVertical;
      const children = filterByTagName(elem.children, 'div');

      const firstItem = children[0];
      const secondItem = children[1];
      setStyles(secondItem, {
        position: 'absolute',
        'overflow-x': 'hidden',
        top: 0,
        bottom: 0,
        left: 0,
      });

      setStyles(firstItem, {
        position: 'absolute',
        'overflow-x': 'hidden',
        top: 0,
        bottom: 0,
        left: 0,
      });

      setStyles(secondItem, { right: 0 });

      setStyles(elem, {
        overflow: 'hidden',
        position: (elem.style.position === 'absolute' ? 'absolute' : 'relative'),
      });

      if (isVertical) {
        firstItem.style.bottom = '0';
        secondItem.style.top = '0';
      } else {
        firstItem.style.right = '0';
        secondItem.style.left = '0';
      }

      elem.firstItem = firstItem;
      elem.secondItem = secondItem;
      elem.percent = 50;

      const divider = document.createElement('div');
      const handle = document.createElement('div');
      handle.className = 'split-panel__handle';

      let indicator = document.createElement('i');
      indicator.className = 'icon-cme-draggable-two';
      handle.appendChild(indicator);
      indicator = null;

      divider.appendChild(handle);
      divider.className = isVertical ? 'split-panel--vertical--divider js-vertical-divider' :
        'split-panel--horizontal--divider js-horizontal-divider';
      divider.style.cssText = isVertical ? 'top:0; bottom:0; position:absolute;' :
        'left:0; right:0; position:absolute;';

      divider.onmousedown = (event) => {
        addClass(elem.splitter, 'dragged');
        this.currentElement = elem;
        if (event) {
          event.preventDefault();
        }
        return false;
      };

      elem.splitter = divider;
      elem.appendChild(divider);
      if (window.innerWidth <= MAGIC_BREAKPOINT) {
        this.setResponsiveLayout(elem);
      } else {
        this.update(elem);
      }
    });

    addEvent(document, 'mousemove', this.onMouseMoveEvent.bind(this));
    addEvent(document, 'mouseup', this.onMouseUpEvent.bind(this));
    for (const launchPanelButton of this.launchPanelButtons) {
      addEvent(launchPanelButton, 'click', this.launchPanel);
    }
    for (const backToMainPanelButton of this.backToMainPanelButtons) {
      addEvent(backToMainPanelButton, 'click', this.backToMainPanel);
    }

    this.windowResizeListener = this.onWindowResize.bind(this);
    this.windowResize = new Throttle(this.windowResizeListener, TIMEOUT_RESIZE_INTERVAL);
    addEvent(window, 'resize', this.windowResize.trigger);
  }

  onWindowResize() {
    for (const index in this.resizes) {
      if (window.innerWidth <= MAGIC_BREAKPOINT) {
        this.setResponsiveLayout(this.resizes[index]);
      } else {
        this.update(this.resizes[index]);
      }
    }
  }

  setResponsiveLayout(element) {
    if (element.classList) {
      addClass(element.splitter, 'hide');
      if (element.classList.contains('split-panel--stacked')) {
        setStyles(element.firstItem, { position: 'relative', left: 0, width: '100%', bottom: '0' });
        setStyles(element.secondItem, { position: 'relative', left: 0, width: '100%', top: '0', transform: 0 });
        setStyles(element, { 'overflow': 'auto' });
      } else {
        setStyles(element.firstItem, { position: 'absolute', left: 0, width: '100%', bottom: '0' });
        setStyles(element.secondItem, { position: 'absolute', left: 0, width: '100%', top: '0' });
        setStyles(element, { 'overflow': 'hidden' });
      }
      element.isInResponsiveState = true;
    }
  }

  unsetResponsiveLayout(element) {
    if (element) {
      removeClass(element.splitter, 'hide');
      setStyles(element, { 'overflow': 'hidden' });
      setStyles(element.firstItem, { position: 'absolute', left: 0, width: 'inherit' });
      setStyles(element.secondItem, { position: 'absolute', left: '50%', width: 'inherit' });
      element.secondItem.style.removeProperty('transform');
      element.isInResponsiveState = false;
    }
  }

  onMouseUpEvent() {
    if (this.currentElement) {
      this.currentElement.splitter.className = this.currentElement.vertical ?
        'split-panel--vertical--divider js-vertical-divider' :
        'split-panel--horizontal--divider js-horizontal-divider';
      this.currentElement = null;
      triggerEvent(this.splitPanel, 'resized.commonui.splitpanel');
    }
  }

  onMouseMoveEvent(event) {
    if (this.currentElement) {
      const isVerticalState = this.currentElement.vertical;

      const splitterState = isVerticalState ? this.currentElement.splitter.offsetWidth :
        this.currentElement.splitter.offsetHeight;
      const elementState = isVerticalState ? this.currentElement.clientWidth : this.currentElement.clientHeight;
      const newPos = (isVerticalState ? event.clientX : event.clientY) -
        this.currentElement.getBoundingClientRect()[isVerticalState ? 'left' : 'top'] - splitterState / 2;
      const newPercentage = (newPos > elementState - splitterState ? elementState - splitterState : newPos);
      this.currentElement.percent = (newPos < 0 ? 0 : newPercentage) / elementState * PERCENTAGE;
      this.update(this.currentElement);

      if (event) {
        event.preventDefault();
      }
      return false;
    }

    return true;
  }

  update(element) {
    if (!element.splitter) {
      return;
    }
    const elementState = element.vertical ? element.clientWidth : element.clientHeight;
    const newPos = element.percent / PERCENTAGE * elementState;
    const splitterWidth = element.vertical ? element.splitter.offsetWidth : element.splitter.offsetHeight;
    const bPercentage = `${ ((newPos + splitterWidth) / elementState * PERCENTAGE) }%`;
    const aPercentage = `${ ((elementState - newPos) / elementState * PERCENTAGE) }%`;
    element.splitter.style[element.vertical ? 'left' : 'top'] = `${ element.percent }%`;
    if (element.isInResponsiveState) {
      this.unsetResponsiveLayout(element);
    }
    if (element.vertical) {
      setStyles(element.secondItem, { left: bPercentage, right: 0, top: 0, bottom: 0 });
      setStyles(element.firstItem, { right: aPercentage, left: 0, top: 0, bottom: 0 });
    } else {
      setStyles(element.secondItem, { top: bPercentage, bottom: 0, left: 0, right: 0 });
      setStyles(element.firstItem, { bottom: aPercentage, top: 0, left: 0, right: 0 });
    }
  }

  launchPanel(event) {
    const secondaryPanel = event.target.parentElement.parentElement
      .parentElement.querySelector('.split-panel--secondary');
    secondaryPanel.style.transform = 'translateX(0)';
  }

  backToMainPanel(event) {
    event.target.parentElement.parentElement.style.transform = 'translateX(100%)';
  }
}

export default function initSplitPanel(options = {}) {
  options = { ...DEFAULT_SPLIT_PANEL_OPTIONS, ...options };
  return new SplitPanel(options);
}



// WEBPACK FOOTER //
// ./js/components/SplitPanel.js